// Custom by Mr Shoe

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
        this._snapping = false; // prevent recursive snapping
        this._availability3 = null; // { by1: Map<option1, Set<option2>>, by12: Map<option1, Map<option2, Set<option3>>> }
      }

      // --------- helpers (NEW/UPDATED) ---------
      getVariantsData() {
        if (this.variants.length) return this.variants;
        const el = this.querySelector('script[data-variants]');
        if (el) {
          try { this.variants = JSON.parse(el.textContent.trim()); }
          catch (e) { console.warn('variant-selects: cannot parse variants JSON', e); }
        }
        return this.variants;
      }

      getOptionContainer(idx) {
        return this.querySelector(`[data-option-position="${idx}"]`) ||
               this.querySelectorAll('.product-form__input')[idx] || null;
      }

      getOptionInputs(container) {
        if (!container) return [];
        const select = container.querySelector('select');
        if (select) return [select];
        return Array.from(container.querySelectorAll('input[type="radio"]'));
      }

      getSelectedValue(container) {
        const inputs = this.getOptionInputs(container);
        if (!inputs.length) return null;
        const el = inputs[0];
        if (el.tagName === 'SELECT') return el.value;
        const checked = inputs.find(i => i.checked);
        return checked ? checked.value : null;
      }

      setSelectedValue(container, value) {
        const inputs = this.getOptionInputs(container);
        if (!inputs.length) return;

        const first = inputs[0];
        if (first.tagName === 'SELECT') {
          if (first.value === value) return;
          first.value = value;
          this.updateSelectedSwatchValue({ target: first });
          first.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const match = inputs.find(i => i.value === value);
        if (!match || match.checked) return;
        match.checked = true;
        this.updateSelectedSwatchValue({ target: match });
        match.dispatchEvent(new Event('change', { bubbles: true }));
      }

      getOptionOrder(idx) {
        const c = this.getOptionContainer(idx);
        const inputs = this.getOptionInputs(c);
        if (!inputs.length) return [];
        const first = inputs[0];
        if (first.tagName === 'SELECT') return Array.from(first.options).map(o => o.value);
        return inputs.map(i => i.value);
      }

      getAvailabilityIndex3() {
        if (this._availability3) return this._availability3;

        const by1 = new Map();                     // option1 -> Set(option2 that exist with ANY option3)
        const by12 = new Map();                    // option1 -> Map(option2 -> Set(option3))
        for (const v of this.getVariantsData()) {
          if (!v.available) continue;
          const o1 = v.option1 || '';
          const o2 = v.option2 || '';
          const o3 = v.option3 || '';

          if (!by1.has(o1)) by1.set(o1, new Set());
          by1.get(o1).add(o2);

          if (!by12.has(o1)) by12.set(o1, new Map());
          if (!by12.get(o1).has(o2)) by12.get(o1).set(o2, new Set());
          // If there is no third option on the product, we still add an empty string sentinel.
          by12.get(o1).get(o2).add(o3);
        }
        this._availability3 = { by1, by12 };
        return this._availability3;
      }

      findNearestValue(optionIndex, desired, allowedSet) {
        const order = this.getOptionOrder(optionIndex);
        if (!order.length || !allowedSet || !allowedSet.size) return null;

        let start = order.indexOf(desired);
        if (start < 0) start = 0;
        if (allowedSet.has(order[start])) return order[start];

        for (let d = 1; d < order.length; d++) {
          const left = start - d;
          const right = start + d;
          if (left >= 0 && allowedSet.has(order[left])) return order[left];
          if (right < order.length && allowedSet.has(order[right])) return order[right];
        }
        return order.find(v => allowedSet.has(v)) || null;
      }

      // --------- your existing methods (kept) ---------
      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.getVariantsData(); // prime variant data

        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);

          if (!this._snapping) {
            const changedWrap = target.closest('.product-form__input');
            const opt0 = this.getOptionContainer(0);
            const opt1 = this.getOptionContainer(1);
            const opt2 = this.getOptionContainer(2);

            if (opt0 && changedWrap && opt0.contains(changedWrap)) {
              // Option 1 changed -> snap Option 2, then Option 3 (if present)
              this._snapSecondAndThird();
            } else if (opt1 && changedWrap && opt1.contains(changedWrap)) {
              // Option 2 changed -> snap Option 3 (if present)
              this._snapThirdOnly();
            }
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      _snapSecondAndThird() {
        const opt1C = this.getOptionContainer(0);
        const opt2C = this.getOptionContainer(1);
        const opt3C = this.getOptionContainer(2);
        if (!opt1C || !opt2C) return;

        const o1 = this.getSelectedValue(opt1C);
        const { by1, by12 } = this.getAvailabilityIndex3();

        // --- snap Option 2 ---
        const allowed2 = by1.get(o1) || new Set();
        const cur2 = this.getSelectedValue(opt2C);
        if (!allowed2.has(cur2)) {
          const replacement2 = this.findNearestValue(1, cur2, allowed2);
          if (replacement2) {
            this._snapping = true;
            this.setSelectedValue(opt2C, replacement2);
            this._snapping = false;
          }
        }

        // --- snap Option 3 (if present) ---
        if (opt3C) {
          const chosen2 = this.getSelectedValue(opt2C);
          const allowed3 = (by12.get(o1)?.get(chosen2)) || new Set();
          // If product actually only has 2 options, allowed3 may contain only '' sentinel.
          // Only proceed if we detect real values in UI.
          const cur3 = this.getSelectedValue(opt3C);
          if (cur3 != null && allowed3.size && !allowed3.has(cur3)) {
            const replacement3 = this.findNearestValue(2, cur3, allowed3);
            if (replacement3) {
              this._snapping = true;
              this.setSelectedValue(opt3C, replacement3);
              this._snapping = false;
            }
          }
        }
      }

      _snapThirdOnly() {
        const opt1C = this.getOptionContainer(0);
        const opt2C = this.getOptionContainer(1);
        const opt3C = this.getOptionContainer(2);
        if (!opt1C || !opt2C || !opt3C) return;

        const { by12 } = this.getAvailabilityIndex3();
        const o1 = this.getSelectedValue(opt1C);
        const o2 = this.getSelectedValue(opt2C);
        const allowed3 = (by12.get(o1)?.get(o2)) || new Set();

        const cur3 = this.getSelectedValue(opt3C);
        if (!allowed3.has(cur3)) {
          const replacement3 = this.findNearestValue(2, cur3, allowed3);
          if (replacement3) {
            this._snapping = true;
            this.setSelectedValue(opt3C, replacement3);
            this._snapping = false;
          }
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }
          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
